import React, { useState, useEffect } from 'react';
import { api, endpoints } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { ReactComponent as UserIcon } from '../../icons/user.svg';
import '../admin/OrganizationsPage.css'; // Aynı CSS'i kullan

const UsersPage = () => {
  const { logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    organization: '',
    role: 'org_user',
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await api.get(endpoints.admin.organizations.list);
      const orgsData = response.results || response || [];
      setOrganizations(orgsData);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(endpoints.admin.users.list);
      const usersData = response.results || response || [];
      setUsers(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.error || 'Kullanıcılar yüklenemedi');
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post(endpoints.admin.users.list, formData);
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      console.error('Error details:', err.response?.data);
      const errorMessage = err.response?.data?.error 
        || err.response?.data?.email?.[0] 
        || Object.values(err.response?.data || {}).flat().join(', ')
        || 'Kullanıcı oluşturulamadı';
      setError(errorMessage);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await api.patch(
        endpoints.admin.users.detail(selectedUser.id),
        formData
      );
      setShowEditModal(false);
      resetForm();
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.error || 'Kullanıcı güncellenemedi');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.patch(endpoints.admin.users.detail(user.id), {
        is_active: !user.is_active
      });
      fetchUsers();
    } catch (err) {
      console.error('Error toggling user status:', err);
      setError(err.response?.data?.error || 'Durum güncellenemedi');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      organization: user.organization,
      role: user.role,
      is_active: user.is_active
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await api.delete(endpoints.admin.users.detail(selectedUser.id));
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.error || 'Kullanıcı silinemedi');
      setShowDeleteModal(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      organization: '',
      role: 'org_user',
      is_active: true
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Statistics
  const stats = {
    total: users.length,
    superAdmins: users.filter(u => u.role === 'super_admin').length,
    orgAdmins: users.filter(u => u.role === 'org_admin').length,
    orgUsers: users.filter(u => u.role === 'org_user').length
  };

  // Filtered users
  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get organization name
  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org ? org.name : 'N/A';
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'purple';
      case 'org_admin': return 'blue';
      case 'org_user': return 'green';
      default: return 'gray';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'org_admin': return 'Org Admin';
      case 'org_user': return 'Kullanıcı';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Page Header */}
      <div className="page-header">
        <h1>Kullanıcı Yönetimi</h1>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={fetchUsers}>
            ↻ Yenile
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + Yeni Kullanıcı
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>Kapat</button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card blue">
          <h3>Toplam Kullanıcı</h3>
          <p className="stat-value">{stats.total}</p>
        </div>
        <div className="stat-card purple">
          <h3>Super Admin</h3>
          <p className="stat-value">{stats.superAdmins}</p>
        </div>
        <div className="stat-card green">
          <h3>Org Admin</h3>
          <p className="stat-value">{stats.orgAdmins}</p>
        </div>
        <div className="stat-card orange">
          <h3>Org User</h3>
          <p className="stat-value">{stats.orgUsers}</p>
        </div>
      </div>

      {/* Table Container */}
      <div className="table-container">
        <div className="table-header">
          <h2>Tüm Kullanıcılar</h2>
          <div className="search-box">
            <input
              type="text"
              placeholder="Kullanıcı ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <UserIcon />
            <h3>Kullanıcı Bulunamadı</h3>
            <p>Henüz hiç kullanıcı oluşturulmamış</p>
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              İlk Kullanıcıyı Oluştur
            </button>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>E-posta</th>
                <th>Organizasyon</th>
                <th>Rol</th>
                <th>Durum</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="org-name">
                      <UserIcon style={{ width: 20, height: 20 }} />
                      {user.email}
                    </div>
                  </td>
                  <td>{getOrgName(user.organization)}</td>
                  <td>
                    <span className={`subdomain-badge`} style={{
                      background: getRoleBadgeColor(user.role) === 'purple' ? '#f3e8ff' : 
                                 getRoleBadgeColor(user.role) === 'blue' ? '#dbeafe' :
                                 getRoleBadgeColor(user.role) === 'green' ? '#d1fae5' : '#f3f4f6',
                      color: getRoleBadgeColor(user.role) === 'purple' ? '#8b5cf6' :
                             getRoleBadgeColor(user.role) === 'blue' ? '#3b82f6' :
                             getRoleBadgeColor(user.role) === 'green' ? '#059669' : '#6b7280'
                    }}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      <span className="status-indicator"></span>
                      {user.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon edit"
                        onClick={() => openEditModal(user)}
                        title="Düzenle"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon delete"
                        onClick={() => openDeleteModal(user)}
                        title="Sil"
                      >
                        🗑️
                      </button>
                      <button
                        className={`btn btn-${user.is_active ? 'secondary' : 'success'}`}
                        style={{ fontSize: '12px', padding: '6px 12px' }}
                        onClick={() => handleToggleActive(user)}
                      >
                        {user.is_active ? 'Devre Dışı Bırak' : 'Aktif Et'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Yeni Kullanıcı Oluştur</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="modal-body">
                <div className="form-group">
                  <label>E-posta *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="user@example.com"
                  />
                </div>
                <small className="form-group-hint">
                  Şifre yoktur — kullanıcı bu e-postaya ait Microsoft hesabıyla giriş yapar.
                </small>
                <div className="form-group">
                  <label>Organizasyon *</label>
                  <select
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Seçiniz...</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Rol *</label>
                  <select name="role" value={formData.role} onChange={handleChange}>
                    <option value="org_user">Kullanıcı (Org User)</option>
                    <option value="org_admin">Org Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      id="is_active_create"
                    />
                    <label htmlFor="is_active_create" style={{ margin: 0 }}>
                      Kullanıcıyı aktif olarak oluştur
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Oluştur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Kullanıcıyı Düzenle</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="modal-body">
                <div className="form-group">
                  <label>E-posta *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Organizasyon *</label>
                  <select
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Seçiniz...</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Rol *</label>
                  <select name="role" value={formData.role} onChange={handleChange}>
                    <option value="org_user">Kullanıcı (Org User)</option>
                    <option value="org_admin">Org Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      id="is_active_edit"
                    />
                    <label htmlFor="is_active_edit" style={{ margin: 0 }}>
                      Kullanıcı aktif
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>⚠️ Kullanıcıyı Sil</h2>
              <button className="close-btn" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ 
                background: '#fee2e2', 
                border: '1px solid #fecaca', 
                borderRadius: '8px', 
                padding: '16px', 
                marginBottom: '20px' 
              }}>
                <p style={{ margin: 0, color: '#991b1b', fontWeight: 500 }}>
                  ⚠️ Bu işlem geri alınamaz!
                </p>
              </div>
              <p style={{ fontSize: '15px', color: '#4b5563', marginBottom: '16px' }}>
                <strong>{selectedUser.email}</strong> kullanıcısını silmek üzeresiniz.
              </p>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                Kullanıcı bilgileri:
              </p>
              <ul style={{ fontSize: '14px', color: '#6b7280', marginLeft: '20px' }}>
                <li>Rol: <strong>{selectedUser.role === 'super_admin' ? 'Süper Admin' : selectedUser.role === 'org_admin' ? 'Organizasyon Admin' : 'Kullanıcı'}</strong></li>
                <li>Organizasyon: <strong>{organizations.find(o => o.id === selectedUser.organization)?.name || 'Bilinmiyor'}</strong></li>
                <li>Durum: <strong>{selectedUser.is_active ? 'Aktif' : 'Pasif'}</strong></li>
              </ul>
              <p style={{ fontSize: '14px', color: '#dc2626', fontWeight: 600, marginTop: '16px' }}>
                Silmek istediğinizden emin misiniz?
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                İptal
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteUser}>
                🗑️ Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
